import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from "firebase/firestore";
import { useLearningProfileStore } from "../../lib/stores/learningProfileStore";
import { Icons } from "../../lib/Icons";

// Example profiles for preview mode (unregistered users)
const EXAMPLE_PROFILES = [
    {
        id: 'preview-1',
        name: 'Year 3 Autism Support',
        curriculumApproach: 'NC_WITH_SUPPORT',
        profileAnswers: { ageGroup: 'ks2' },
        recommendedActivityIds: Array(12).fill(''),
        updatedAt: { toDate: () => new Date() },
    },
    {
        id: 'preview-2',
        name: 'EYFS Communication Focus',
        curriculumApproach: 'ENGAGEMENT_MODEL',
        profileAnswers: { ageGroup: 'eyfs' },
        recommendedActivityIds: Array(8).fill(''),
        updatedAt: { toDate: () => new Date() },
    },
    {
        id: 'preview-3',
        name: 'KS3 SEMH Support',
        curriculumApproach: 'NC_WITH_SUPPORT',
        profileAnswers: { ageGroup: 'ks3' },
        recommendedActivityIds: Array(15).fill(''),
        updatedAt: { toDate: () => new Date() },
    },
];

const LearningProfilesList = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profiles, setProfiles] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);

    const { reset } = useLearningProfileStore();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                // Show preview mode for unregistered users instead of redirecting
                setIsPreviewMode(true);
                setProfiles(EXAMPLE_PROFILES);
                setLoading(false);
                return;
            }
            setUser(currentUser);
            setIsPreviewMode(false);
            await loadProfiles(currentUser.uid);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    const loadProfiles = async (userId) => {
        try {
            const q = query(
                collection(db, 'learningProfiles'),
                where('userId', '==', userId),
                orderBy('updatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const profilesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setProfiles(profilesList);
        } catch (err) {
            console.error('Error loading profiles:', err);
            // If index doesn't exist yet, try without orderBy
            try {
                const q = query(
                    collection(db, 'learningProfiles'),
                    where('userId', '==', userId)
                );
                const snapshot = await getDocs(q);
                const profilesList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setProfiles(profilesList.sort((a, b) =>
                    (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0)
                ));
            } catch (e) {
                console.error('Fallback query also failed:', e);
            }
        }
    };

    const handleDelete = async (profileId) => {
        try {
            await deleteDoc(doc(db, 'learningProfiles', profileId));
            setProfiles(prev => prev.filter(p => p.id !== profileId));
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Error deleting profile:', err);
        }
    };

    const handleNewProfile = () => {
        if (isPreviewMode) {
            navigate('/signup/solo');
            return;
        }
        reset();
        navigate('/solo/profiles/new');
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500"></div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/solo/dashboard" className="flex items-center gap-3">
                            <img
                                src="/brand/White logo on purple.png"
                                alt="SEN-Ed"
                                className="w-10 h-10 rounded-xl"
                            />
                            <span className="text-xl font-bold text-white">SEN-Ed</span>
                        </Link>

                        <Link
                            to="/solo/dashboard"
                            className="text-slate-400 hover:text-white transition-colors"
                        >
                            ← Back to Dashboard
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Preview Mode Banner */}
                {isPreviewMode && (
                    <div className="mb-8 p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl border border-purple-500/30">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-500/30 flex items-center justify-center">
                                <Icons.Sparkles className="w-6 h-6 text-purple-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-white mb-1">Preview Mode</h3>
                                <p className="text-slate-300 text-sm">
                                    These are example learning profiles. Sign up to create personalized profiles for your students
                                    and get AI-powered activity recommendations based on their needs.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/signup/solo')}
                                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-2"
                            >
                                <Icons.LockClosed className="w-4 h-4" />
                                Sign Up Free
                            </button>
                        </div>
                    </div>
                )}

                {/* Page Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Learning Profiles</h1>
                        <p className="text-slate-400">
                            Build and manage learning profiles to get personalized activity recommendations.
                        </p>
                    </div>
                    <button
                        onClick={handleNewProfile}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl font-medium transition-colors"
                    >
                        {isPreviewMode ? (
                            <Icons.LockClosed className="w-5 h-5" />
                        ) : (
                            <Icons.Plus className="w-5 h-5" />
                        )}
                        {isPreviewMode ? 'Sign Up to Create' : 'New Profile'}
                    </button>
                </div>

                {/* Profiles Grid */}
                {profiles.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {profiles.map(profile => (
                            <div
                                key={profile.id}
                                className="bg-slate-800 rounded-2xl p-6 hover:bg-slate-750 transition-colors group"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <Link
                                        to={`/solo/profiles/${profile.id}`}
                                        className="flex-1"
                                    >
                                        <h3 className="text-lg font-semibold text-white group-hover:text-purple-300 transition-colors">
                                            {profile.name || 'Untitled Profile'}
                                        </h3>
                                    </Link>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setDeleteConfirm(profile.id)}
                                            className="p-2 text-slate-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            <Icons.XCircle className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <Link to={`/solo/profiles/${profile.id}`}>
                                    {/* Curriculum Approach Badge */}
                                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4 ${profile.curriculumApproach === 'ENGAGEMENT_MODEL'
                                        ? 'bg-purple-500/20 text-purple-300'
                                        : 'bg-blue-500/20 text-blue-300'
                                        }`}>
                                        <Icons.Sparkles className="w-3 h-3" />
                                        {profile.curriculumApproach === 'ENGAGEMENT_MODEL'
                                            ? 'Engagement Model'
                                            : 'NC with Support'}
                                    </div>

                                    {/* Quick Info */}
                                    <div className="space-y-2 text-sm text-slate-400 mb-4">
                                        {profile.profileAnswers?.ageGroup && (
                                            <div className="flex items-center gap-2">
                                                <Icons.User className="w-4 h-4" />
                                                <span>
                                                    {profile.profileAnswers.ageGroup === 'eyfs' ? 'Early Years' :
                                                        profile.profileAnswers.ageGroup === 'ks1' ? 'KS1' :
                                                            profile.profileAnswers.ageGroup === 'ks2' ? 'KS2' :
                                                                profile.profileAnswers.ageGroup === 'ks3' ? 'KS3' :
                                                                    profile.profileAnswers.ageGroup === 'ks4' ? 'KS4' :
                                                                        profile.profileAnswers.ageGroup === 'post16' ? 'Post-16' :
                                                                            profile.profileAnswers.ageGroup}
                                                </span>
                                            </div>
                                        )}
                                        {profile.recommendedActivityIds && (
                                            <div className="flex items-center gap-2">
                                                <Icons.BookOpen className="w-4 h-4" />
                                                <span>{profile.recommendedActivityIds.length} activities matched</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Date */}
                                    <div className="text-xs text-slate-500">
                                        Updated {formatDate(profile.updatedAt)}
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-slate-800 rounded-2xl p-12 text-center">
                        <div className="w-16 h-16 bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <Icons.ClipboardDocumentList className="w-8 h-8 text-slate-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">No profiles yet</h3>
                        <p className="text-slate-400 mb-6 max-w-md mx-auto">
                            Create a learning profile to get personalized activity recommendations for your students.
                        </p>
                        <button
                            onClick={handleNewProfile}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-xl font-medium transition-colors"
                        >
                            <Icons.Plus className="w-5 h-5" />
                            Create Your First Profile
                        </button>
                    </div>
                )}
            </main>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-red-500/20 rounded-xl">
                                <Icons.ExclamationTriangle className="w-6 h-6 text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Delete Profile?</h3>
                                <p className="text-slate-400 text-sm">This action cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirm)}
                                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LearningProfilesList;
